import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Handshake, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import SearchableSelect from '@/components/common/SearchableSelect';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { apiMessage } from '@/lib/pricing';

type WaitingRow = {
  organization_id: string;
  name: string;
  trial_state: 'active' | 'ending_soon' | 'expired' | null;
  trial_ends_at: string | null;
  days_left: number | null;
  cloud_servers: number;
  legal_company_name: string;
  billing_contact_name: string;
  billing_contact_email: string;
  invoicing_status: string;
  region: string | null;
  region_name: string | null;
};

type SentRow = {
  id: string;
  organization_id: string;
  organization_name: string;
  plan_name: string;
  message: string;
  sent_by_email: string;
  emailed_to: string;
  sent_at: string;
};

type PlanRow = {
  id: string;
  name: string;
  description: string;
  features: string[];
  region: string;
  region_name: string;
  region_currency: string;
};

type EnterpriseClients = { waiting: WaitingRow[]; sent: SentRow[]; plans: PlanRow[] };

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function trialLabel(row: WaitingRow) {
  if (row.trial_state === 'expired') return 'Trial ended';
  if (row.days_left != null) return `${row.days_left} day${row.days_left === 1 ? '' : 's'} left`;
  return 'On trial';
}

export default function EnterpriseClientsPage() {
  const queryClient = useQueryClient();
  const [sendingTo, setSendingTo] = React.useState<WaitingRow | null>(null);
  const [planId, setPlanId] = React.useState('');
  const [message, setMessage] = React.useState('');

  const clients = useQuery({
    queryKey: ['enterprise-clients'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/enterprise-clients/')).data as EnterpriseClients;
    },
  });

  const sendPlan = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/global-admin/enterprise-clients/', {
        organization: sendingTo!.organization_id,
        plan: planId,
        message,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-clients'] });
      queryClient.invalidateQueries({ queryKey: ['global-admin-customers'] });
      toast.success(`${sendingTo?.name} is on the plan and has been told.`);
      setSendingTo(null);
      setPlanId('');
      setMessage('');
    },
    onError: (err: any) => toast.error(apiMessage(err)),
  });

  const openSend = (row: WaitingRow) => {
    setSendingTo(row);
    setPlanId(clients.data?.plans[0]?.id ?? '');
    setMessage('');
  };

  const chosenPlan = clients.data?.plans.find((plan) => plan.id === planId);
  // A plan is priced in one region, so offering one from another would send
  // a customer a plan whose prices can never apply to them.
  const plansForClient = (clients.data?.plans ?? []).filter(
    (plan) => !sendingTo?.region || plan.region === sendingTo.region,
  );
  const noPlans = !clients.isLoading && (clients.data?.plans.length ?? 0) === 0;
  const noPlansHere = !noPlans && sendingTo != null && plansForClient.length === 0;
  const waiting = clients.data?.waiting ?? [];
  const sent = clients.data?.sent ?? [];

  return (
    <div className="space-y-6" data-testid="enterprise-clients-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Enterprise clients</h2>
        <p className="text-muted-foreground">
          Who is trialling on the enterprise tier with nothing agreed yet, and
          what has been sent to whom. A plan takes effect the moment it is sent.
        </p>
      </div>

      <Card data-testid="waiting-for-a-plan">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Handshake className="h-4 w-4" />
            Waiting for a plan
            <Badge variant={waiting.length ? 'default' : 'secondary'} className="ml-1">
              {waiting.length}
            </Badge>
          </CardTitle>
          <CardDescription>
            Enterprise trials with no plan of their own. Until one is sent they
            are quoted small business prices, and they cannot be approved for
            invoicing at all.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {clients.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : waiting.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground" data-testid="nobody-waiting">
              Nobody is waiting. Every enterprise trial is on a plan.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Trial</TableHead>
                    <TableHead className="text-right">Cloud servers</TableHead>
                    <TableHead>Who to talk to</TableHead>
                    <TableHead className="w-40" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {waiting.map((row) => (
                    <TableRow key={row.organization_id} data-testid={`waiting-${row.organization_id}`}>
                      <TableCell>
                        <Link to={`/customers/${row.organization_id}`} className="font-medium hover:underline">
                          {row.name}
                        </Link>
                        {row.legal_company_name && (
                          <div className="text-xs text-muted-foreground">{row.legal_company_name}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={row.trial_state === 'expired' ? 'destructive' : row.trial_state === 'ending_soon' ? 'default' : 'secondary'}>
                          {trialLabel(row)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{row.cloud_servers}</TableCell>
                      <TableCell className="text-sm">
                        {row.billing_contact_email ? (
                          <>
                            <div>{row.billing_contact_name || '—'}</div>
                            <div className="text-xs text-muted-foreground">{row.billing_contact_email}</div>
                          </>
                        ) : (
                          <span className="text-muted-foreground">No billing contact yet</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" onClick={() => openSend(row)} data-testid={`send-plan-${row.organization_id}`}>
                          <Send className="mr-2 h-3.5 w-3.5" />
                          Send a plan
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="plans-already-sent">
        <CardHeader>
          <CardTitle>Plans already sent</CardTitle>
          <CardDescription>
            Every plan sent to a customer, what was said with it, and whether the
            email reached anyone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Nothing has been sent yet.</p>
          ) : (
            <ol className="space-y-3" data-testid="sent-plan-list">
              {sent.map((row) => (
                <li key={row.id} className="rounded-md border p-3 text-sm" data-testid={`sent-plan-${row.id}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span>
                      <Link to={`/customers/${row.organization_id}`} className="font-medium hover:underline">
                        {row.organization_name}
                      </Link>
                      <span className="text-muted-foreground"> is on </span>
                      <span className="font-medium">{row.plan_name}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {when(row.sent_at)} · {row.sent_by_email || 'a global admin'}
                    </span>
                  </div>
                  {row.message && <p className="mt-1 text-muted-foreground">{row.message}</p>}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {row.emailed_to
                      ? `Emailed to ${row.emailed_to}`
                      : 'No billing contact to email; they were told in their conversation only'}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <Dialog open={sendingTo != null} onOpenChange={(open) => { if (!open) setSendingTo(null); }}>
        <DialogContent data-testid="send-plan-dialog">
          <DialogHeader>
            <DialogTitle>Send a plan to {sendingTo?.name}</DialogTitle>
            <DialogDescription>
              They move to the enterprise tier at once and the plan's prices
              apply to servers they start from today. Servers they already run
              keep the price they started on.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Plan</Label>
              {noPlansHere ? (
                <div
                  className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                  data-testid="no-plans-for-region"
                >
                  <p>
                    {sendingTo?.name} is billed in {sendingTo?.region_name}, and no plan is
                    priced there.
                  </p>
                  <p className="mt-1">
                    A plan belongs to one region, so make one for {sendingTo?.region_name} on the{' '}
                    <Link to="/enterprise-plans" className="font-medium underline">
                      Enterprise plans
                    </Link>{' '}
                    page.
                  </p>
                </div>
              ) : noPlans ? (
                <div
                  className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                  data-testid="no-plans-yet"
                >
                  <p>There are no enterprise plans to send yet.</p>
                  <p className="mt-1">
                    Create one on the{' '}
                    <Link to="/enterprise-plans" className="font-medium underline">
                      Enterprise plans
                    </Link>{' '}
                    page, then come back and send it.
                  </p>
                </div>
              ) : (
                <SearchableSelect
                  value={planId}
                  onChange={setPlanId}
                  placeholder="Choose a plan"
                  searchPlaceholder="Find a plan…"
                  options={plansForClient.map((plan) => ({ value: plan.id, label: plan.name, hint: plan.region_currency }))}
                  testId="send-plan-choice"
                />
              )}
              {chosenPlan && (
                <div className="rounded-md border bg-muted/30 p-3 text-xs" data-testid="send-plan-summary">
                  {chosenPlan.description && <p>{chosenPlan.description}</p>}
                  <p className="mt-1 text-muted-foreground">
                    {chosenPlan.features.length
                      ? `Unlocks ${chosenPlan.features.join(', ')}.`
                      : 'No extra features.'}
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    Anything the plan does not price falls back to the enterprise
                    tier, and then to small business. Price it on the Pricing page.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="send-plan-message">What to say to them</Label>
              <Textarea
                id="send-plan-message"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="As agreed on the call…"
                data-testid="send-plan-message"
              />
              <p className="text-xs text-muted-foreground">
                {sendingTo?.billing_contact_email
                  ? `Emailed to ${sendingTo.billing_contact_email} and posted to their conversation.`
                  : 'They have no billing contact, so this goes to their conversation only.'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSendingTo(null)}>Cancel</Button>
            <Button
              onClick={() => sendPlan.mutate()}
              disabled={sendPlan.isPending || !planId}
              data-testid="send-plan-confirm"
            >
              {sendPlan.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Send the plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
